const localStorageKey = 'ATT_ACT_STABLE_ID';

export const HAS_WINDOW = typeof window !== 'undefined';

/**
 * Safely gets a property from the window object
 * @param {string} key property on window to retrieve
 */
export function safeGetWindowObject<Key extends keyof Window>(key: Key): Window[Key] | null {
    return HAS_WINDOW ? window[key] : null;
}

export const getAttributionIDFromLocalStorage = () =>
    safeGetWindowObject('localStorage')?.getItem(localStorageKey);

export const setAttributionIDInLocalStorage = (value: string) =>
    safeGetWindowObject('localStorage')?.setItem(localStorageKey, value);

export const deleteAttributionIDInLocalStorage = () =>
    safeGetWindowObject('localStorage')?.removeItem(localStorageKey);

// Organization IDs that have access to server-side tracking configuration
const SERVER_SIDE_TRACKING_ALLOWED_ORGS: string[] = [
    'test-123456789', // tests org
    'org_123456789', // tests org
    '315264916821', // Nate QA org
    '310549367431', // Lyja QA org
    '315862943911', // Anton QA org
    '310893701011', // Ethan QA org
    '310917683723', // Jake QA org
    // Production Beta orgs
    '228289580634', // The Latin Social
    '279614226731', // Yeah Buoy Events
    '209772825361', // Bar Pop
    '975978377133', // Second Life Markets (1 of 2)
    '1943070161533', // Second Life Markets (2 of 2)
    '316024262290', // Authentic Autographs
    '172745631726', // Eat The Beat
    '347014776913', // Lucky Presents (Techno)
    '240431900032', // Gargantuan
    // Add more organization IDs here as needed
];

export const useAttributionID = () => {
    const stableId = getAttributionIDFromLocalStorage() || generateUUID();

    if (stableId) {
        try {
            setAttributionIDInLocalStorage(stableId);
        } catch (err) {}
    }

    return stableId;
};

export const isServerSideTrackingEnabledForOrganization = (
    organizationId: string,
    isServerSideEnabled: boolean,
) => {
    if (isServerSideEnabled) {
        return true;
    } else if (!organizationId) {
        return false;
    } else {
        return SERVER_SIDE_TRACKING_ALLOWED_ORGS.includes(organizationId);
    }
};

/**
 * Generates a UUID v4. Uses crypto.randomUUID() if available (modern browsers),
 * otherwise falls back to a manual implementation.
 * @returns {string} A UUID v4 string
 */
export const generateUUID = (): string => {
    // Use browser's crypto.randomUUID() if available (Chrome 92+, Firefox 95+, Safari 15.4+)
    // Check at runtime, not module load time, to handle SSR environments
    if (
        typeof window !== 'undefined' &&
        typeof window.crypto !== 'undefined' &&
        typeof window.crypto.randomUUID === 'function'
    ) {
        return window.crypto.randomUUID();
    }

    // Fallback for older browsers - generate UUID v4 manually
    // Based on RFC 4122: https://www.ietf.org/rfc/rfc4122.txt
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
};
